/**
 * Unified modal for sequence links: prerequisite (preceding) vs dependent (succeeding).
 * Stores assertion as dependency or prerequisite edge per canon mirror model.
 */

import { useState, useEffect } from 'react';
import { X, GitBranch, Flag, AlertCircle } from 'lucide-react';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { SearchableItemPicker } from '../SearchableItemPicker';
import { DEFAULT_SEQUENCE_STRENGTH, SEQUENCE_STRENGTH_OPTIONS, findMirrorPair } from '../../../lib/sequenceRelationships';
import type { DependencySubtype } from '../../../types/prioritization/itemRelationships';

export type SequenceLinkMode = 'add_predecessor' | 'add_dependent';

interface AddSequenceLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (edgeId: string) => void;
  /** Current item — vantage for the sequence link. */
  anchorItemId?: string;
  mode?: SequenceLinkMode;
}


const MODE_CONFIG: Record<
  SequenceLinkMode,
  {
    title: string;
    subtitle: string;
    otherLabel: string;
    otherPlaceholder: string;
    Icon: typeof GitBranch;
    iconClass: string;
    buttonClass: string;
    successLabel: string;
  }
> = {
  add_predecessor: {
    title: 'Link prerequisite',
    subtitle: 'Choose what must come before this item (preceding card)',
    otherLabel: 'Prerequisite item',
    otherPlaceholder: 'Search for the item that must come first…',
    Icon: Flag,
    iconClass: 'text-[var(--text-ter)]',
    buttonClass: 'bg-[var(--slate)] hover:bg-[var(--slate)]',
    successLabel: 'Prerequisite link created',
  },
  add_dependent: {
    title: 'Link dependent',
    subtitle: 'Choose what waits on this item (succeeding card)',
    otherLabel: 'Dependent item',
    otherPlaceholder: 'Search for the item that waits on this one…',
    Icon: GitBranch,
    iconClass: 'text-[var(--text-ter)]',
    buttonClass: 'bg-[var(--slate)] hover:bg-[var(--slate)]',
    successLabel: 'Dependent link created',
  },
};

export function AddSequenceLinkModal({
  isOpen,
  onClose,
  onCreated,
  anchorItemId,
  mode: initialMode,
}: AddSequenceLinkModalProps) {
  const createEdge = useItemRelationshipsStore((s) => s.createEdge);
  const edges = useItemRelationshipsStore((s) => s.edges);
  const fetchFromSupabase = useItemRelationshipsStore((s) => s.fetchFromSupabase);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);

  const [anchorId, setAnchorId] = useState<string>(anchorItemId ?? '');
  const [mode, setMode] = useState<SequenceLinkMode>(initialMode ?? 'add_predecessor');
  const [otherId, setOtherId] = useState<string>('');
  const [subtype, setSubtype] = useState<DependencySubtype>(DEFAULT_SEQUENCE_STRENGTH);
  const [label, setLabel] = useState('');
  const [rationale, setRationale] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [mirrorWarning, setMirrorWarning] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const config = MODE_CONFIG[mode];
  const { Icon } = config;

  useEffect(() => {
    if (isOpen) {
      void fetchItems();
      void fetchFromSupabase();
    }
  }, [isOpen, fetchItems, fetchFromSupabase]);

  useEffect(() => {
    if (!isOpen) return;
    setAnchorId(anchorItemId ?? '');
    setOtherId('');
    setSubtype(DEFAULT_SEQUENCE_STRENGTH);
    setLabel('');
    setRationale('');
    setError(null);
    setMirrorWarning(null);
    setMode(initialMode ?? 'add_predecessor');
  }, [isOpen, anchorItemId, initialMode]);

  useEffect(() => {
    if (!anchorId || !otherId || anchorId === otherId) {
      setMirrorWarning(null);
      return;
    }
    const predecessorId = mode === 'add_predecessor' ? otherId : anchorId;
    const dependentId = mode === 'add_predecessor' ? anchorId : otherId;
    const mirror = findMirrorPair(edges, predecessorId, dependentId);
    if (mirror) {
      setMirrorWarning(
        'A mirror link already exists on this pair (both Dependency and Prerequisite). You can still add another, but consider dissolving the redundant edge.',
      );
    } else {
      setMirrorWarning(null);
    }
  }, [anchorId, otherId, mode, edges]);

  const handleCreate = async (ignoreMirror = false) => {
    setError(null);
    if (!anchorId) {
      setError('Select this item (anchor)');
      return;
    }
    if (!otherId) {
      setError(config.otherLabel + ' is required');
      return;
    }
    if (anchorId === otherId) {
      setError('An item cannot link to itself');
      return;
    }

    const predecessorId = mode === 'add_predecessor' ? otherId : anchorId;
    const dependentId = mode === 'add_predecessor' ? anchorId : otherId;

    if (!ignoreMirror && findMirrorPair(edges, predecessorId, dependentId)) {
      setMirrorWarning(
        'Mirror pair detected. Confirm to add a redundant assertion, or cancel and dissolve the existing mirror edge.',
      );
      return;
    }

    setIsCreating(true);
    try {
      const edgeInput =
        mode === 'add_predecessor'
          ? {
              type: 'dependency' as const,
              subtype,
              directionality: 'directed' as const,
              status: 'active' as const,
              declaredMode: 'declared' as const,
              sourceItemId: dependentId,
              targetItemId: predecessorId,
              label: label.trim() || undefined,
              rationale: rationale.trim() || undefined,
            }
          : {
              // The same strength, whichever end asserts the link (canon 12 §5.4).
              type: 'prerequisite' as const,
              subtype,
              directionality: 'directed' as const,
              status: 'active' as const,
              declaredMode: 'declared' as const,
              sourceItemId: predecessorId,
              targetItemId: dependentId,
              rationale: rationale.trim() || undefined,
            };

      const edge = await createEdge(edgeInput);
      if (edge) {
        onCreated?.(edge.id);
        onClose();
      } else {
        setError('Failed to create sequence link');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create sequence link');
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  const canSubmit = !!anchorId && !!otherId && anchorId !== otherId && !isCreating;
  const showAnchorPicker = !anchorItemId;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <Icon className={`h-6 w-6 ${config.iconClass}`} />
            <div>
              <h2 className="text-xl font-semibold text-[var(--text-pri)]">{config.title}</h2>
              <p className="text-sm text-[var(--text-ter)]">{config.subtitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--hover-tint)]">
            <X className="h-5 w-5 text-[var(--text-ter)]" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {mirrorWarning && (
            <div className="flex flex-col gap-2 p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-[var(--mustard)] text-sm">
              <p>{mirrorWarning}</p>
              <button
                type="button"
                onClick={() => handleCreate(true)}
                disabled={isCreating}
                className="text-xs font-medium text-[var(--mustard)] underline hover:no-underline disabled:opacity-50"
              >
                Add anyway
              </button>
            </div>
          )}

          {!initialMode && (
            <div>
              <p className="text-sm font-medium text-[var(--text-sec)] mb-2">What are you adding?</p>
              <div className="grid grid-cols-2 gap-2">
                {([
                  ['add_predecessor', 'Must be addressed before this item', 'Add a predecessor'],
                  ['add_dependent', 'An item waiting on this item', 'Add a dependent'],
                ] as const).map(([value, title, description]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => { setMode(value); setOtherId(''); setMirrorWarning(null); }}
                    className={` border p-3 text-left transition-colors ${
                      mode === value ? 'border-[var(--slate)] bg-[var(--slate-dim)]' : 'border-[var(--rule)] hover:border-[var(--rule)]'
                    }`}
                  >
                    <span className="block text-sm font-medium text-[var(--text-pri)]">{title}</span>
                    <span className="block text-xs text-[var(--text-ter)] mt-0.5">{description}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-3">
            {showAnchorPicker && (
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">
                  This item <span className="text-[var(--text-ter)] font-normal">(anchor)</span>
                </label>
                <SearchableItemPicker
                  value={anchorId || undefined}
                  onChange={(id) => setAnchorId(id ?? '')}
                  excludeId={otherId || undefined}
                  label=""
                  placeholder="Search for anchor item…"
                  lifecycleStates={[]}
                  allowEmpty={false}
                  allowCreate
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">
                {config.otherLabel}
              </label>
              <SearchableItemPicker
                value={otherId || undefined}
                onChange={(id) => setOtherId(id ?? '')}
                excludeId={anchorId || undefined}
                label=""
                placeholder={config.otherPlaceholder}
                lifecycleStates={[]}
                allowEmpty={false}
                allowCreate
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Strength</label>
            <div className="space-y-2">
              {SEQUENCE_STRENGTH_OPTIONS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setSubtype(t.value)}
                  className={`w-full p-3  border-2 text-left ${
                    subtype === t.value ? 'border-[var(--slate)] bg-[var(--slate-dim)]' : 'border-[var(--rule)] hover:border-[var(--rule)]'
                  }`}
                >
                  <div className="font-medium text-sm text-[var(--text-pri)]">{t.label}</div>
                  <div className="text-xs text-[var(--text-ter)] mt-0.5">{t.description}</div>
                </button>
              ))}
            </div>
          </div>

          {mode === 'add_predecessor' && (
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Label (optional)</label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Blocked by"
                className="w-full px-4 py-2 border border-[var(--rule)]"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Rationale (optional)</label>
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="Why must this sequence hold?"
              rows={2}
              className="w-full px-4 py-2 border border-[var(--rule)] resize-none"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-[var(--surface)]">
          <button onClick={onClose} className="px-4 py-2 text-[var(--text-sec)] font-medium hover:bg-[var(--hover-tint)]">
            Cancel
          </button>
          <button
            onClick={() => handleCreate(false)}
            disabled={!canSubmit}
            className={`px-4 py-2 text-white font-medium  disabled:opacity-50 disabled:cursor-not-allowed ${config.buttonClass}`}
          >
            {isCreating ? 'Adding…' : 'Add link'}
          </button>
        </div>
      </div>
    </div>
  );
}
